import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useCultureHubQueryParams } from '@/areas/culture-hub/hooks/useCultureHubQueryParams';
import { useDashboardCardsData } from '@/areas/culture-hub/components/analytics/dashboard/DashboardCardsLayout';
import { useGetCultureHubAnalyticsQuery } from '@/areas/culture-hub/api/cultureHubApi';
import { useApiPortalV1AssessmentsResultsListQuery } from '@/areas/assessment-schedule/api';
import MobileAssessmentPeriodList from './MobileAssessmentPeriodList';
import MobileTopBarSelectors from '@/mobile/components/MobileTopBarSelectors';
import {
  getAssessmentPeriods,
  getCurrentOrganizationId,
  getLeadershipTeamsForSelectedAssessmentPeriod,
  getMemberId,
  getSelectedAssessmentPeriodId,
} from 'common/selectors/auth';
import { Dropdown, type DropdownProps } from 'semantic-ui-react';
import userContextSlice from 'common/slices/userContextSlice';
import { MobileSpinner } from '@/mobile/layouts/common/MobilePageStyles';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';
import MobileScoreGaugeCard from './MobileScoreGaugeCard';
import MobileScoresChart from './MobileScoresChart';
import MobileDashboardCardsLayout from './MobileDashboardCardsLayout';
import { Divider } from 'semantic-ui-react';

import { AggregateType } from '@/common/constants/AggregateType';
const MobileCultureHubDashboardPage: React.FC = () => {
  const {
    assessmentPeriodId,
    teamId,
    shouldFetchData,
    isAssessmentPeriodsLoading,
    organizationId,
  } = useCultureHubQueryParams();
  const { overallScore, differential, previousScore, isLoaded: cardsLoaded } = useDashboardCardsData();
  const dispatch = useDispatch();
  const assessmentPeriods = useSelector(getAssessmentPeriods);
  const selectedAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const currentOrganizationId = useSelector(getCurrentOrganizationId);
  const selectedTeamId = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;
  const leadershipTeams = useSelector(getLeadershipTeamsForSelectedAssessmentPeriod);
  const currentUserId = useSelector(getMemberId);
  const { setTopBarContent } = React.useContext(TopBarContext);

  const handleTeamChange = React.useCallback(
    (value: string | number | undefined) => {
      const nextTeamId = typeof value === 'string' ? Number(value) : value;
      if (nextTeamId && nextTeamId !== selectedTeamId) {
        dispatch(userContextSlice.actions.setSelectedTeamId(nextTeamId));
      }
    },
    [dispatch, selectedTeamId]
  );

  const handleAssessmentPeriodChange = React.useCallback(
    (value: string | number | undefined, period?: any) => {
      const selected = period ?? assessmentPeriods?.find((p: any) => p.id === value);
      if (selected && selectedAssessmentPeriodId !== selected.id) {
        dispatch(
          userContextSlice.actions.setSelectedAssessmentPeriod({
            selectedAssessmentPeriod: selected,
            assessmentPeriods,
          })
        );
      }
    },
    [assessmentPeriods, dispatch, selectedAssessmentPeriodId]
  );

  React.useEffect(() => {
    if ((selectedTeamId === undefined || selectedTeamId === null) && teamId != null) {
      dispatch(userContextSlice.actions.setSelectedTeamId(teamId));
    }
  }, [dispatch, selectedTeamId, teamId]);

  React.useEffect(() => {
    setTopBarContent(
      <MobileTopBarSelectors
        teamDropdown={
          leadershipTeams?.length ? (
            <Dropdown
              className="mobile-control-dropdown"
              placeholder="Select a team"
              fluid
              selection
              options={leadershipTeams.map(team => ({
                key: team.id,
                value: team.id,
                text: team.name,
              }))}
              value={teamId ?? undefined}
              onChange={(_event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                const nextTeamId = data.value as string | number | undefined;
                handleTeamChange(nextTeamId);
              }}
              style={{
                width: '100%',
                border: 'none',
                boxShadow: 'none',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '18px',
                fontWeight: 550,
                minHeight: '40px',
                lineHeight: '20px',
                paddingLeft: '10px',
                paddingTop: '6px',
                paddingBottom: '6px',
              }}
            />
          ) : undefined
        }
        periodDropdown={
          <MobileAssessmentPeriodList
            className="mobile-control-dropdown"
            currentOrganizationId={currentOrganizationId}
            selectedAssessmentPeriodId={selectedAssessmentPeriodId}
            onChange={handleAssessmentPeriodChange}
          />
        }
      />
    );

    return () => {
      setTopBarContent(null);
    };
  }, [
    leadershipTeams,
    teamId,
    handleTeamChange,
    currentOrganizationId,
    selectedAssessmentPeriodId,
    handleAssessmentPeriodChange,
    setTopBarContent,
  ]);

  const { data: analyticsData, isLoading, isFetching } = useGetCultureHubAnalyticsQuery(
    { assessmentPeriodId: assessmentPeriodId!, teamId },
    { skip: !assessmentPeriodId || !shouldFetchData }
  );

  const { data: ai_sentiment_data, isLoading: aiLoading, isFetching: aiFetching } = useApiPortalV1AssessmentsResultsListQuery(
    {
      aggregateType: AggregateType.TEAM_SUMMARY,
      assessmentPeriodId: assessmentPeriodId!,
      employeeId: currentUserId ?? undefined,
    },
    { skip: !assessmentPeriodId }
  );

  const { isLoading: scoreSentimentLoading, isFetching: scoreSentimentFetching } = useApiPortalV1AssessmentsResultsListQuery(
    {
      aggregateType: AggregateType.SENTIMENT_DISTRIBUTION,
      assessmentPeriodId: assessmentPeriodId!,
      employeeId: currentUserId ?? undefined,
    },
    { skip: !assessmentPeriodId }
  );

  const { aiSummary, teamName } = useMemo(() => {
    const summaries = ai_sentiment_data?.[0]?.result?.summaries;
    if (!summaries || !teamId) return { aiSummary: undefined, teamName: undefined };

    const found = summaries.find((s: any) => Number(s.team_id) === Number(teamId));
    if (!found) return { aiSummary: undefined, teamName: undefined };

    const teamName = found.team_name ?? undefined;
    const aiSummary = found.summary ? { summary: found.summary, bullets: found.bullets } : undefined;
    return { aiSummary, teamName };
  }, [ai_sentiment_data, teamId]);

  const fetching =
    Boolean(!cardsLoaded && shouldFetchData) ||
    isLoading ||
    isFetching ||
    aiLoading ||
    aiFetching ||
    scoreSentimentLoading ||
    scoreSentimentFetching;

  if (isAssessmentPeriodsLoading) {
    return <MobileSpinner />;
  }

  return (
    <PageWrapper>
      <SectionTitle>Cultural Assessment</SectionTitle>
      {!assessmentPeriodId ? (
        <MessageContainer>
          Select an assessment period to view Culture Hub analytics.
        </MessageContainer>
      ) : !teamId ? (
        <MessageContainer>
          Team details are required to load mobile Culture Hub analytics.
        </MessageContainer>
      ) : fetching ? (
        <MobileSpinner />
      ) : (
        <MobileDashboardCardsLayout>
          <MobileScoreGaugeCard
            overallScore={overallScore ?? 0}
            differential={differential}
            previousScore={previousScore}
            assessmentPeriodId={assessmentPeriodId}
            teamId={teamId}
            employeeId={currentUserId ?? undefined}
            aiSummary={aiSummary}
            teamName={teamName}
            pillars={analyticsData ? Object.values(analyticsData.pillars || {}) : []}
          />
          <Divider />
          <MobileScoresChart
            pillars={analyticsData ? Object.values(analyticsData.pillars || {}) : []}
            teamId={teamId}
            assessmentPeriodId={assessmentPeriodId}
            teamName={teamName}
          />
        </MobileDashboardCardsLayout>
      )}
    </PageWrapper>
  );
};

export default MobileCultureHubDashboardPage;

const PageWrapper = styled.div`
  display: grid;
  gap: 24px;
`;

const SectionTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: #111827;
`;

const MessageContainer = styled.div`
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  color: #334155;
  text-align: center;
`;





